import React, { useMemo, useState } from "react";
import PublicShell from "@/layouts/PublicShell";
import StateBadge from "@/components/StateBadge";

/**
 * Screen Revenue Calculator
 * Inputs → Outputs across Conservative / Expected / High scenarios.
 * "Do not imply guaranteed revenue."
 */

const SCENARIOS = {
  conservative: { label: "Conservative", visibility: 0.25, sellthrough: 0.35, priceMul: 0.85, color: "#a78bfa" },
  expected: { label: "Expected", visibility: 0.4, sellthrough: 0.55, priceMul: 1.0, color: "#E8D6A3" },
  high: { label: "High utilisation", visibility: 0.6, sellthrough: 0.75, priceMul: 1.2, color: "#CCFF00" },
};

export default function Calculator() {
  const [footfall, setFootfall] = useState(2500);
  const [dwell, setDwell] = useState(20);
  const [hours, setHours] = useState(12);
  const [slots, setSlots] = useState(6);
  const [adDuration, setAdDuration] = useState(10);
  const [cpm, setCpm] = useState(18);
  const [scenario, setScenario] = useState("expected");

  const s = SCENARIOS[scenario];

  const monthly = useMemo(() => {
    // Monthly available impressions ≈ footfall * visibility * hours * (slots/adDuration) * 30
    // (simplified — not a promise. calibrate later against real telemetry)
    const impressionsPerDay = Math.round(footfall * s.visibility * slots);
    const monthlyImpressions = impressionsPerDay * 30;
    const soldImpressions = Math.round(monthlyImpressions * s.sellthrough);
    const revenue = Math.round((soldImpressions / 1000) * cpm * s.priceMul);
    return { impressionsPerDay, monthlyImpressions, soldImpressions, revenue };
  }, [footfall, dwell, hours, slots, adDuration, cpm, s]);

  return (
    <PublicShell>
      <section className="pt-28 md:pt-32 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 pb-14">
          <div className="flex items-center gap-3">
            <StateBadge state="BETA" />
            <span className="text-[10px] uppercase tracking-[0.22em] text-white/40">Screen Revenue Calculator</span>
          </div>
          <h1 className="mt-6 font-display font-black tracking-tighter leading-[0.9] text-5xl md:text-7xl">
            What could this screen earn?
          </h1>
          <p className="mt-6 max-w-2xl text-white/60 text-lg">
            Screen economics = <span className="text-white">Audience × Attention × Inventory × Sell-through × Pricing × Trust</span>.
            Estimates — never guarantees.
          </p>
        </div>
      </section>

      <section className="border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-14 grid lg:grid-cols-[1fr_1fr] gap-10 items-start">
          {/* Inputs */}
          <div className="space-y-6" data-testid="calculator-inputs">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2">Scenario</div>
              <div className="flex gap-1">
                {Object.entries(SCENARIOS).map(([k, v]) => (
                  <button
                    key={k}
                    onClick={() => setScenario(k)}
                    data-testid={`calculator-scenario-${k}`}
                    className={`flex-1 px-3 py-2 border transition text-xs uppercase tracking-[0.18em] font-bold ${
                      scenario === k
                        ? "border-[#E8D6A3] text-[#E8D6A3] bg-[#D4B878]/10"
                        : "border-white/10 text-white/50 hover:border-white/30"
                    }`}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
            </div>

            <NumInput label="Daily footfall" value={footfall} setValue={setFootfall} min={100} max={200000} step={100} testid="calculator-footfall" />
            <NumInput label="Average dwell time (sec)" value={dwell} setValue={setDwell} min={2} max={300} step={1} testid="calculator-dwell" />
            <NumInput label="Operating hours / day" value={hours} setValue={setHours} min={1} max={24} step={1} testid="calculator-hours" />
            <NumInput label="Ad slots / hour" value={slots} setValue={setSlots} min={1} max={60} step={1} testid="calculator-slots" />
            <NumInput label="Ad duration (sec)" value={adDuration} setValue={setAdDuration} min={5} max={60} step={1} testid="calculator-duration" />
            <NumInput label="Indicative CPM ($)" value={cpm} setValue={setCpm} min={1} max={200} step={1} testid="calculator-cpm" />
          </div>

          {/* Outputs */}
          <div className="lg:sticky lg:top-24 space-y-4" data-testid="calculator-outputs">
            <div className="p-8 border border-[#E8D6A3]/30 bg-gradient-to-br from-[#1a1508] to-[#0d0c0b]">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#E8D6A3] mb-2">
                Estimated monthly revenue · {s.label}
              </div>
              <div className="font-mono font-black text-6xl tracking-tight" data-testid="calculator-revenue">
                ${monthly.revenue.toLocaleString()}
              </div>
              <div className="text-xs text-white/40 mt-3">
                Annualised: ~${(monthly.revenue * 12).toLocaleString()}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-px bg-white/10 border border-white/10">
              <KPI k="Impressions / day" v={monthly.impressionsPerDay.toLocaleString()} />
              <KPI k="Monthly available" v={monthly.monthlyImpressions.toLocaleString()} />
              <KPI k="Monthly sold" v={monthly.soldImpressions.toLocaleString()} accent />
            </div>
            <div className="p-4 border border-white/10 bg-white/[0.02]">
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2">
                Important
              </div>
              <div className="text-xs text-white/60 leading-relaxed">
                These are illustrative estimates only. Actual revenue depends
                on real audience, verified attention, fill rate, market
                pricing, brand-safety, and settlement terms.
              </div>
            </div>
          </div>
        </div>
      </section>
    </PublicShell>
  );
}

function NumInput({ label, value, setValue, min, max, step, testid }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] uppercase tracking-[0.22em] text-white/40">{label}</div>
        <input
          type="number"
          value={value}
          min={min}
          max={max}
          step={step}
          onChange={(e) => setValue(+e.target.value || 0)}
          data-testid={testid}
          className="w-24 bg-black border border-white/10 px-2 py-1 text-right text-white font-mono text-sm"
        />
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => setValue(+e.target.value)}
        className="w-full accent-[#E8D6A3]"
      />
    </div>
  );
}

function KPI({ k, v, accent }) {
  return (
    <div className="bg-[#0A0908] p-4">
      <div className="text-[10px] uppercase tracking-[0.22em] text-white/40">{k}</div>
      <div className={`font-mono text-lg font-bold mt-1 ${accent ? "text-[#E8D6A3]" : "text-white"}`}>{v}</div>
    </div>
  );
}
